import { createRef, Component, Fragment } from "react";
import { func, object, string } from "prop-types";

import { routes } from "../../constants";
import { scrollIfNeeded, validateInput } from "../../helpers";

import Alert from "../../styles/components/Alert";
import {
  AuthForm,
  AuthFormButton,
  AuthFormActions,
  AuthFormLink,
} from "../../styles/components/AuthForm";
import Group from "../form/components/Group";
import Label from "../form/components/Label";
import Meta from "../meta";
import Text from "../form/components/Text";

class Login extends Component {
  constructor(props) {
    super(props);

    this.cleanup = this.cleanup.bind(this);
    this.handleApiError = this.handleApiError.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.postData = this.postData.bind(this);

    this.alerts = createRef();
    this.form = createRef();

    this.state = {
      emailError: null,
      inProgress: false,
      passwordError: null,
      redirect: { pathname: routes.results.path },
      serverError: null,
    };
  }

  /**
   * Cleanup auth message if user leaves this page
   * @public
   */
  cleanup() {
    const { authMessage, clearAuthMessage } = this.props;

    if (!!authMessage) {
      clearAuthMessage();
    }

    window.removeEventListener("beforeunload", this.componentCleanup);
  }

  /**
   * Handle API error
   * @param {*}
   * @public
   */
  handleApiError(error) {
    // Error message is parsed in the redux action
    const message = (!!error && error.message) || "Error";

    this.setState({ inProgress: false, serverError: message }, () =>
      scrollIfNeeded(this.alerts.current)
    );
  }

  /**
   * Handle form submission
   * @param {event} e
   * @public
   */
  handleSubmit(e) {
    e.preventDefault();

    const { form } = this;
    const errors = {};
    const data = {};
    let hasError = false;

    if (!!form.current) {
      [...form.current.elements].forEach(input => {
        if (!!input.id) {
          const error = validateInput(input) || null;
          hasError = !!error ? true : hasError;
          errors[`${input.id}Error`] = error;
          data[input.id] = !!input ? input.value : null;
        }
      });
    }

    this.setState(errors);

    if (hasError) {
      scrollIfNeeded(this.alerts.current);
    } else {
      this.postData(data);
    }
  }

  /**
   * Post data to API
   * @param {object} data
   * @public
   */
  postData(data) {
    this.setState({ inProgress: true });

    const { handleApiError } = this;
    const { login } = this.props;

    login(data).then(response => {
      if (response.success) {
        this.props.history.push(this.state.redirect);
      } else {
        handleApiError(response);
      }
    });
  }

  componentDidMount() {
    const { authMessage, location, logout } = this.props;

    if (!!location.state && !!location.state.from) {
      this.setState({
        redirect: location.state.from,
        serverError: !!location.state.errorMessage
          ? location.state.errorMessage
          : null,
      });

      // Set initial value for email field if available
      if (!!location.state.email) {
        document.getElementById("email").value = location.state.email;
      }
    }

    if (!authMessage) {
      logout();
    }

    window.prerenderReady = true;
    window.addEventListener("beforeunload", this.cleanup);
  }

  componentWillUnmount() {
    this.cleanup();
  }

  render() {
    const { form, handleSubmit, alerts } = this;
    const { authMessage, location } = this.props;
    const { emailError, inProgress, passwordError, serverError } = this.state;

    return (
      <Fragment>
        <Meta title="Login" />
        <div ref={alerts}>
          {!!authMessage && !serverError && <Alert>{authMessage}</Alert>}
          {!!serverError && <Alert type="error">{serverError}</Alert>}
        </div>
        <AuthForm formRef={form} onSubmit={handleSubmit}>
          <Group error={emailError}>
            <Label htmlFor="email" required>
              Email
            </Label>
            <Text id="email" maxLength="200" required type="email" />
          </Group>
          <Group error={passwordError} labelOffset="smaller">
            <Label htmlFor="password" required>
              Password
            </Label>
            <Text id="password" required type="password" />
          </Group>
          <AuthFormActions>
            <AuthFormButton disabled={inProgress}>
              {inProgress ? "Logging in…" : "Login"}
            </AuthFormButton>
          </AuthFormActions>
          <AuthFormLink
            to={{ pathname: routes.forgot.path, state: { ...location.state } }}
          >
            Forgot Password
          </AuthFormLink>
        </AuthForm>
      </Fragment>
    );
  }
}

Login.propTypes = {
  authMessage: string,
  clearAuthMessage: func.isRequired,
  history: object.isRequired,
  location: object.isRequired,
  login: func.isRequired,
  logout: func.isRequired,
};

export default Login;
